import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box, Flex, Text, Center, Button, Table, TableContainer, Th, Td, Thead, Tbody, Tr, Tfoot, useDisclosure } from '@chakra-ui/react';
import BreadcrumbComponent from '../../../components/BreadcrumbComponent/BreadcrumbComponent';
import { faPrint, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from '../../../constant';
import { gql, useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import useStore from '../../../store';
import PrintLayout from '../../../components/Print/PrintLayout';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import getOfficeLabel from '../../../utils/getOfficeLabel';
import getShortDepartmentLabel from '../../../utils/getDepartmentLabel';
import OfficialListFilter from '../../../components/Filter/OfficialListFilter';
import ExportExcelOfficialsRank from './excel/excelRank';
import getDepartmentLabel from '../../../utils/getDepartmentLabel';

const QUERY_EMPLOYEES = gql`
	query getEmployees($offset: Float, $limit: Float, $name: String, $isActive: Boolean, $department: String, $office: String, $examCandidates: Boolean) {
		getEmployees(
			name: $name
			department: $department
			office: $office
			examCandidates: $examCandidates
			isActive: $isActive
			pagerInput: { offset: $offset, limit: $limit }
		) {
			totalRows
			totalPages
			employees {
				id
				department
				office
				govtNumber
				fullName
				fullNameLatin
				gender
				dob
				createdAt
				phoneNumber
				currentWorkingStatus
				firstWorkingPlace
				additionalRoleOnCurrentJob
				active
				pob
				isPobManual
				pobManual
				currentAddress
				isCurrentAddressManual
				currentAddressManual
				email
				degree
				idCardNumber
				idCardExpirationDate
				spouseStatus
				childInformation
				generalEducation
				specialization
				inTheFieldOfPublicFunctions
				inThePrivateSector
				foreignLanguage
				active
				examCandidates
				positionStatuses {
					id
					rank
					position
					dateOfLastRank
					dateOfLastPosition
					attachmentFile
				}
				latestPositionStatus {
					id
					rank
					position
					dateOfLastRank
					dateOfLastPosition
					attachmentFile
				}
			}
		}
	}
`;

const limit = 9999;

function OfficialsListRank() {
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { currentLang: lang } = useStore((state) => state.currentLang);
	moment.locale(lang === 'kh' ? 'km' : 'en-gb');
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const department = urlParams.get('department');
	const examCandidates = urlParams.get('examCandidates');
	const office = urlParams.get('office');
	const status = urlParams.get('status');
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [username, setUsername] = useState('');
	const currentPage = urlParams.get('page') ? urlParams.get('page') : 1;
	const [loadEmployees, { loading, data }] = useLazyQuery(QUERY_EMPLOYEES);

	const pageNumber = Number(currentPage) || 1;

	const employees = useMemo(() => {
		return data?.getEmployees?.employees || [];
	}, [data]);

	useEffect(() => {
		loadEmployees({
			variables: {
				offset: (pageNumber - 1) * limit,
				limit,
				name: username,
				isActive: status === 'active' ? true : status === 'inactive' ? false : null,
				department,
				office,
				examCandidates: examCandidates === 'true',
			},
		});
	}, [loadEmployees, pageNumber, username, status, department, office, examCandidates]);

	function formatDateOrDash(date) {
		if (!date || !moment(date).isValid()) {
			return '-';
		}

		return moment(date).format('DD MMM YYYY');
	}

	function getRankDate(employee, rank) {
		const records = employee?.positionStatuses || [];
		const findRankDate = records?.find((record) => record?.rank === rank);

		if (findRankDate?.dateOfLastRank) {
			return formatDateOrDash(findRankDate?.dateOfLastRank);
		}
	}

	function callbackFilter(e) {
		history.push(`/officials-list-rank${e}`);
	}

	return (
		<>
			<PrintLayout>
				<>
					<Container maxW="container.xl" mt="16px" mb="16px">
						<BreadcrumbComponent
							list={[
								{
									name: 'Official Profile',
									path: `/employee/list?page=1`,
								},
								{
									name: 'List',
									path: `#`,
								},
							]}
						/>
						<Center mt="5">
							<Box
								w="100%"
								bg="white"
								borderRadius={c.borderRadius}
								border="1px solid #dbdbdb"
								boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
								pb="32px"
							>
								<Flex
									alignItems="center"
									justifyContent="space-between"
									bg="#FAFAFA"
									p="16px"
									pt="8px"
									pb="8px"
									mb="16px"
									borderBottom="1px solid #dbdbdb"
								>
									<Flex justifyContent="center" alignItems="center">
										<Text fontSize="x-large" fontWeight="bold" mr="4">
											{t('Officials Rank List')}
										</Text>
									</Flex>

									<Flex className="hidden-print">
										<Button
											onClick={() => onOpen()}
											leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
											colorScheme={`${department || office || examCandidates ? 'yellow' : 'gray'}`}
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Filter')}
										</Button>
										<ExportExcelOfficialsRank employees={employees} ml={2} />
										<Button
											onClick={() => window?.print()}
											leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Print')}
										</Button>
									</Flex>
								</Flex>

								<Box p={4} pt={2}>
									<TableContainer maxH="100vh" overflowY="auto">
										<Table variant="striped" colorScheme="green" size="md">
											<Thead position="sticky" top={0} zIndex={2} bg="white">
												<Tr>
													<Th>{t('Number')}</Th>
													<Th align="left" position="sticky" left="0" zIndex={1} bg="white" maxW="500vw">
														{t('Fullname (Khmer)')}
													</Th>
													<Th>{t('Fullname (Latin)')}</Th>
													<Th align="left">{t('Department')}</Th>
													<Th align="left">{t('Office')}</Th>
													<Th align="left">{t('Gender')}</Th>
													<Th align="left">{t('Date of birth')}</Th>
													<Th align="left">{t('Date of Employment')}</Th>
													<Th align="left">{t('Date as Statute Officer')}</Th>
													<Th align="left">{t('ក្រមការ')}</Th>
													<Th align="left">{t('ក្រមការដើមខ្សែរ')}</Th>
													<Th align="left">{t('នាយក្រមការ')}</Th>
													<Th align="left">{t('អនុមន្រ្តីកម្រិត៣')}</Th>
													<Th align="right">{t('អនុមន្រ្តីកម្រិត២')}</Th>
													<Th align="right">{t('អនុមន្រ្តីកម្រិត១')}</Th>
												</Tr>
											</Thead>

											<Tbody>
												{loading ? (
													<Tr>
														<Td colSpan={14} textAlign="left">
															{t('Loading')}...
														</Td>
													</Tr>
												) : employees?.length === 0 ? (
													<Tr>
														<Td colSpan={14} textAlign="left">
															{t('No Data')}
														</Td>
													</Tr>
												) : (
													employees?.map((employee, index) => {
														const positionStatuses = employee?.positionStatuses || null;
														const statuedOfficerRecord =
															employee?.latestPositionStatus?.position === '7'
																? employee?.latestPositionStatus
																: positionStatuses?.find((record) => record?.position === '7');

														const statuedOfficerDate = statuedOfficerRecord?.dateOfLastPosition
															? formatDateOrDash(statuedOfficerRecord?.dateOfLastPosition)
															: employee?.currentWorkingStatus?.currentWorkingStatusPosition === '7'
																? formatDateOrDash(employee?.currentWorkingStatus?.currentWorkingStatusDateOfChangePosition)
																: '-';

														return (
															<Tr key={employee?.id}>
																<Td>{index + 1}</Td>
																<Td position="sticky" left="0" zIndex={1} bg="white">
																	{employee?.fullName || '-'}
																</Td>
																<Td>{employee?.fullNameLatin || '-'}</Td>
																<Td>{employee?.department}</Td>
																<Td>{employee?.office}</Td>
																<Td>{employee?.gender ? t(employee?.gender) : '-'}</Td>
																<Td>{employee?.dob ? moment(employee?.dob).format('DD MMM YYYY') : '-'}</Td>
																<Td textAlign="center">
																	{employee?.createdAt ? moment(employee?.createdAt).format('DD MMM YYYY') : '-'}
																</Td>

																<Td textAlign="center">{statuedOfficerDate ? statuedOfficerDate : '-'}</Td>

																<Td textAlign="center">{getRankDate(employee, 'Level_2')}</Td>

																<Td textAlign="center">{getRankDate(employee, 'Level_3')}</Td>

																<Td textAlign="center">{getRankDate(employee, 'Level_4')}</Td>

																<Td textAlign="center">{getRankDate(employee, 'Level_5')}</Td>

																<Td textAlign="center">{getRankDate(employee, 'Level_6')}</Td>

																<Td style={{ textAlign: 'right' }}>{getRankDate(employee, 'Level_7')}</Td>
															</Tr>
														);
													})
												)}
											</Tbody>
											<Tfoot>
												<Tr>
													<Th>{t('Number')}</Th>
													<Th align="left">{t('Fullname (Khmer)')}</Th>
													<Th align="left">{t('Fullname (Latin)')}</Th>
													<Th align="left">{t('Department')}</Th>
													<Th align="left">{t('Office')}</Th>
													<Th align="left">{t('Gender')}</Th>
													<Th align="left">{t('Date of birth')}</Th>
													<Th align="left">{t('Date of Employment')}</Th>
													<Th align="left">{t('Date as Statute Officer')}</Th>
													<Th align="left">{t('ក្រមការ')}</Th>
													<Th align="left">{t('ក្រមការដើមខ្សែរ')}</Th>
													<Th align="left">{t('នាយក្រមការ')}</Th>
													<Th align="left">{t('អនុមន្រ្តីកម្រិត៣')}</Th>
													<Th align="right">{t('អនុមន្រ្តីកម្រិត២')}</Th>
													<Th align="right">{t('អនុមន្រ្តីកម្រិត១')}</Th>
												</Tr>
											</Tfoot>
										</Table>
									</TableContainer>
								</Box>
							</Box>
						</Center>
					</Container>

					<OfficialListFilter
						isOpen={isOpen}
						onClose={onClose}
						callback={callbackFilter}
						department={department}
						office={office}
						examCandidates={examCandidates}
					/>
				</>
			</PrintLayout>
		</>
	);
}

export default OfficialsListRank;
